import { useMutation, useQueryClient } from "@tanstack/react-query";
import { assertSuccessfulWorkerResponse } from "./helpers";
import { showSuccessToast } from "../../utils/showToastAlert";

const APPROVAL_ACTIONS = new Set(["accept", "reject"]);

export function usePostParticipantApproval({
  callApiWithWorker,
  t,
  onErrorAlert,
}) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ["participants", "approval", "update"],
    mutationFn: async ({ participantId, action, payload, reason, comment }) => {
      if (!participantId) throw new Error("Missing participantId");

      const normalizedAction = String(action ?? "")
        .trim()
        .toLowerCase();
      if (!APPROVAL_ACTIONS.has(normalizedAction)) {
        throw new Error("Invalid participant approval action");
      }

      const approvalPayload =
        payload && typeof payload === "object"
          ? payload
          : normalizedAction === "reject"
            ? {
                ...(reason ? { reason } : {}),
                ...(comment ? { comment } : {}),
              }
            : null;

      const res = await callApiWithWorker(
        `/registry/participants/${encodeURIComponent(participantId)}/approval/${normalizedAction}`,
        {
          method: "POST",
          connector: "springConnector",
          ...(approvalPayload ? { body: approvalPayload } : {}),
        },
      );

      return assertSuccessfulWorkerResponse(
        res,
        "Update participant approval failed",
      );
    },
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({ queryKey: ["participants"] });
      await qc.invalidateQueries({
        queryKey: ["participants", "byId", vars?.participantId],
      });

      const normalizedAction = String(vars?.action ?? "")
        .trim()
        .toLowerCase();
      showSuccessToast(
        t?.(
          `participant.approval.updated.${normalizedAction}`,
          t?.(
            "participant.approval.updated.default",
            "Participant approval updated successfully",
          ),
        ),
        {
          title: t?.("participant.approval.updatedTitle", "Approval updated"),
        },
      );
    },
    onError: async (err) => {
      if (onErrorAlert) return onErrorAlert(err);
      console.error(t ? t("error") : "Error", err);
    },
  });
}
